import { useEffect, useRef, useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { formatDuration } from '@itr/shared';
import { useRealtime } from '../api/realtime';
import { useToast } from './ui';

/**
 * Short drops (a proxy restart, a Wi-Fi hand-off) recover on their own within
 * a couple of seconds; a popup for those would just be noise. This also covers
 * the first connect on page load.
 */
const GRACE_MS = 5_000;

/**
 * Popup shown while the live connection to the ITR server is down.
 *
 * A monitoring screen that has lost its server keeps showing the last numbers
 * it had, which look exactly like current ones — so this is deliberately in
 * the way rather than a small badge. It can be dismissed (the topbar badge
 * stays), and it comes back on the next separate outage.
 */
export function ConnectionLostDialog() {
  const { connected, disconnectedSince, nextRetryAt, reconnectNow } = useRealtime();
  const toast = useToast();
  const [now, setNow] = useState(() => Date.now());
  const [dismissedOutage, setDismissedOutage] = useState<number | null>(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const shownRef = useRef(false);

  useEffect(() => {
    if (connected) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [connected]);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const outageLongEnough = !connected && disconnectedSince !== null && now - disconnectedSince >= GRACE_MS;
  const open = outageLongEnough && dismissedOutage !== disconnectedSince;

  // Only announce a recovery the user was actually told about.
  useEffect(() => {
    if (outageLongEnough) shownRef.current = true;
    if (connected && shownRef.current) {
      shownRef.current = false;
      toast('Reconnected to the ITR server', 'good', 'Live updates have resumed.');
    }
  }, [connected, outageLongEnough, toast]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissedOutage(disconnectedSince);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, disconnectedSince]);

  if (!open || disconnectedSince === null) return null;

  const retryIn = nextRetryAt ? Math.max(0, Math.ceil((nextRetryAt - now) / 1000)) : null;

  return (
    <>
      <div className="overlay conn-lost-overlay" />
      <div className="conn-lost" role="alertdialog" aria-modal="true" aria-labelledby="conn-lost-title" aria-describedby="conn-lost-body">
        <div className="conn-lost-icon" data-offline={!online || undefined}>
          <WifiOff size={22} />
        </div>
        <h2 id="conn-lost-title" className="conn-lost-title">
          Disconnected from the ITR server
        </h2>
        <p id="conn-lost-body" className="conn-lost-body">
          {online
            ? 'The server is not responding. Alerts, health and numbers on this page are not updating and may be out of date.'
            : 'This device is offline. Check your network connection — alerts, health and numbers on this page are not updating.'}
        </p>
        <div className="conn-lost-meta nums">
          Disconnected for {formatDuration((now - disconnectedSince) / 1000)}
          {' · '}
          {retryIn === null ? 'Reconnecting…' : `Retrying in ${retryIn}s`}
        </div>
        <div className="row" style={{ gap: 8, justifyContent: 'center' }}>
          <button className="btn" onClick={() => setDismissedOutage(disconnectedSince)}>
            Dismiss
          </button>
          <button className="btn btn-primary" onClick={reconnectNow} autoFocus>
            <RefreshCw size={14} /> Retry now
          </button>
        </div>
      </div>
    </>
  );
}
